import React, { useCallback, useEffect, useRef, useState } from 'react'
import './NavigationWithSideBar.scss'
import { NavigationSidebar } from './NavigationSidebar'
import classNames from 'classnames'

export interface ScrollNavigationSection {
  id: string
  label: string
  title?: string
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  content: React.ReactNode
}

export type ScrollNavigationVariant = 'default' | 'compact'

const ACTIVE_SECTION_OFFSET = 100

interface ScrollNavigationProps {
  sections: ScrollNavigationSection[]
  defaultActiveSectionId: string
  scrollToSectionId?: string
  sectionClassName?: string
  showNavigation?: boolean
  contentFooter?: React.ReactNode
  variant?: ScrollNavigationVariant
}

interface ContentAreaProps {
  sections: ScrollNavigationSection[]
  onSectionRegister: (id: string, element: HTMLElement | null) => void
  sectionClassName?: string
  contentFooter?: React.ReactNode
  variant: ScrollNavigationVariant
}

const ContentArea = React.memo(({ sections, onSectionRegister, sectionClassName, contentFooter, variant }: ContentAreaProps) => {
  const compact = variant === 'compact'
  return (
    <div className='scroll-navigation__sections'>
      {sections.map((section, index) => (
        <section
          key={section.id}
          id={section.id}
          ref={(element) => onSectionRegister(section.id, element)}
          className={classNames('scroll-navigation__section', {
            [`${sectionClassName}`]: sectionClassName,
            'zocks-mb-4': !compact,
            'zocks-mb-2': compact,
            'border-top zocks-border-color-gray-200 zocks-pt-2.0': compact && index > 0,
          })}
          data-section-id={section.id}
        >
          <div className={classNames('d-flex justify-content-between mb-3', { 'align-items-start gap-3': !compact, 'align-items-center gap-2': compact })}>
            <div>
              <h2
                className={classNames('zocks-text-gray-900', {
                  h4: !compact,
                  'heading-small': compact,
                  'mb-0': !section.subtitle,
                  'mb-1': section.subtitle && !compact,
                  'mb-2': section.subtitle && compact,
                })}
              >
                {section.title || section.label}
              </h2>
              {section.subtitle ? <div className='paragraph-medium-extended zocks-text-gray-900'>{section.subtitle}</div> : null}
            </div>
            {section.actions ? <div className='flex-shrink-0'>{section.actions}</div> : null}
          </div>
          {section.content}
        </section>
      ))}
      {contentFooter ? <div className='scroll-navigation__content-footer'>{contentFooter}</div> : null}
      <div className='scroll-navigation__spacer'></div>
    </div>
  )
})

export const ScrollNavigation: React.FC<ScrollNavigationProps> = ({
  sections = [],
  defaultActiveSectionId,
  scrollToSectionId,
  sectionClassName,
  showNavigation = true,
  contentFooter,
  variant = 'default',
}) => {
  const [activeSection, setActiveSection] = useState<string>(defaultActiveSectionId)

  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  const handleSectionRegister = useCallback((id: string, element: HTMLElement | null) => {
    sectionRefs.current[id] = element
  }, [])

  // Keyed on the target rather than mount-only: inside a react-bootstrap tab set both panes mount up
  // front, so a hash that arrives while the user is on another tab reaches an already-mounted
  // ScrollNavigation. The frame delay lets the newly-activated pane become visible first —
  // scrollIntoView on a `display: none` pane is a no-op.
  useEffect(() => {
    if (!scrollToSectionId) {
      return
    }
    const frame = requestAnimationFrame(() => {
      setActiveSection(scrollToSectionId)
      sectionRefs.current[scrollToSectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(frame)
  }, [scrollToSectionId])

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId)

    const element = sectionRefs.current[sectionId]

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const currentSection = sections
        .slice()
        .reverse()
        .find((section) => {
          const element = sectionRefs.current[section.id]
          return element && element.getBoundingClientRect().top <= ACTIVE_SECTION_OFFSET
        })

      if (currentSection && activeSection !== currentSection.id) {
        setActiveSection(currentSection.id)
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [sections, activeSection])

  return (
    <div className='scroll-navigation__container'>
      {showNavigation && (
        <NavigationSidebar sections={sections.map(({ id, label }) => ({ id, label }))} activeSectionId={activeSection} onSectionClick={scrollToSection} />
      )}
      <ContentArea
        sections={sections}
        onSectionRegister={handleSectionRegister}
        sectionClassName={sectionClassName}
        contentFooter={contentFooter}
        variant={variant}
      />
    </div>
  )
}
